import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartErrorEvent, CartNoteUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Handles updates to the cart note.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateCartNote = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    const note = event.target.value;
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    // Dispatch the cart note update event before the fetch with a promise for the result
    const isDialog = Boolean(this.closest('dialog'));
    const deferredPromise = CartNoteUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartNoteUpdateEvent({
        context: isDialog ? 'dialog' : 'cart',
        note,
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ note }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({ cart: CartNoteUpdateEvent.createCartFromAjaxResponse(data) });
    } catch (error) {
      deferredPromise.reject(error);
      // Don't dispatch error for user-triggered aborts
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to update cart note',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('note-update:user-action', event);
    }
  }, 200);
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}

/* Trioamico: InPost Paczkomat picker in cart (public InPost Points API) */
const taPl = {
  label: 'Paczkomat InPost',
  info: 'Wybierasz dostawę do Paczkomatu? Wpisz kod pocztowy lub miasto i wybierz automat z listy. W kolejnym kroku wybierz „InPost Paczkomat 24/7”.',
  warn: 'Jeśli nie wybierzesz Paczkomatu, wyślemy paczkę do automatu najbliższego adresowi dostawy. Przy wysyłce kurierem możesz pominąć ten krok.',
  ph: 'Kod pocztowy lub miasto, np. 00-001',
  search: 'Szukaj',
  near: '📍 W pobliżu mnie',
  choose: 'Wybierz',
  chosen: 'Wybrany Paczkomat:',
  change: 'zmień',
  none: 'Nie znaleziono Paczkomatów. Sprawdź kod pocztowy lub miasto.',
  error: 'Nie udało się pobrać listy. Znajdź Paczkomat na mapie InPost i wpisz jego kod poniżej.',
  map: 'Mapa InPost ↗',
  loading: 'Szukam…',
  noteLabel: 'Uwagi / kod Paczkomatu (np. WAW123M)',
};
const taEn = {
  label: 'InPost Paczkomat',
  info: 'Delivery to a parcel locker? Enter a postcode or city and choose a Paczkomat from the list. At checkout, select “InPost Paczkomat 24/7”.',
  warn: 'If you do not choose a Paczkomat, we will send the parcel to the one closest to your delivery address. For courier delivery you can skip this step.',
  ph: 'Postcode or city, e.g. 00-001',
  search: 'Search',
  near: '📍 Near me',
  choose: 'Choose',
  chosen: 'Selected Paczkomat:',
  change: 'change',
  none: 'No Paczkomats found. Check the postcode or city.',
  error: 'Could not load the list. Find a Paczkomat on the InPost map and enter its code below.',
  map: 'InPost map ↗',
  loading: 'Searching…',
  noteLabel: 'Notes / Paczkomat code (e.g. WAW123M)',
};
const TA_API = 'https://api-pl-points.easypack24.net/v1/points';
const taT = () => ((document.documentElement.lang || '').toLowerCase().startsWith('en') ? taEn : taPl);
const taEl = (tag, css, text) => {
  const e = document.createElement(tag);
  if (css) e.style.cssText = css;
  if (text != null) e.textContent = text;
  return e;
};
const taBtn = 'cursor:pointer;border:1px solid #12305c;border-radius:999px;padding:6px 12px;font-size:.8rem;background:#12305c;color:#fff';
const taBtn2 = 'cursor:pointer;border:1px solid #12305c;border-radius:999px;padding:6px 12px;font-size:.8rem;background:#fff;color:#12305c';

async function taSaveChoice(el, p) {
  const t = taT();
  const a = p.address_details || {};
  const addr = [[a.street, a.building_number].filter(Boolean).join(' '), [a.post_code, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const line = 'Paczkomat: ' + p.name + (addr ? ' – ' + addr : '');
  const ta = el.querySelector('textarea');
  let note = ta ? ta.value : '';
  note = note.split('\n').filter((l) => !l.startsWith('Paczkomat: ')).join('\n').trim();
  note = note ? line + '\n' + note : line;
  if (ta) {
    ta.value = note;
    ta.dispatchEvent(new Event('input', { bubbles: true }));
  }
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    await fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note, attributes: { Paczkomat: p.name, 'Paczkomat adres': addr } }),
    });
  } catch (e) {}
  try { sessionStorage.setItem('taPaczkomat', JSON.stringify({ name: p.name, addr })); } catch (e) {}
  taRenderChosen(el, { name: p.name, addr });
  const res = el.querySelector('.ta-pm-res');
  if (res) res.replaceChildren();
}

function taRenderChosen(el, c) {
  const t = taT();
  const box = el.querySelector('.ta-pm-chosen');
  if (!box) return;
  box.replaceChildren();
  if (!c) { box.style.display = 'none'; return; }
  box.style.display = 'block';
  box.append(taEl('strong', '', t.chosen + ' '), taEl('span', '', c.name + (c.addr ? ' – ' + c.addr : '') + ' '));
  const ch = taEl('a', 'cursor:pointer;text-decoration:underline;color:#12305c', '(' + t.change + ')');
  ch.addEventListener('click', () => { const q = el.querySelector('.ta-pm-q'); if (q) q.focus(); });
  box.append(ch);
}

async function taSearch(el, params) {
  const t = taT();
  const res = el.querySelector('.ta-pm-res');
  if (!res) return;
  res.replaceChildren(taEl('p', 'margin:6px 0;font-size:.8rem', t.loading));
  const qs = new URLSearchParams(Object.assign({ type: 'parcel_locker', per_page: '6' }, params));
  try {
    const r = await fetch(TA_API + '?' + qs.toString());
    if (!r.ok) throw new Error('http ' + r.status);
    const data = await r.json();
    const items = (data && data.items) || [];
    res.replaceChildren();
    if (!items.length) { res.append(taEl('p', 'margin:6px 0;font-size:.8rem', t.none)); return; }
    items.forEach((p) => {
      const a = p.address_details || {};
      const row = taEl('div', 'display:flex;gap:8px;align-items:center;justify-content:space-between;border:1px solid #d9e1ee;border-radius:8px;padding:8px;margin-top:6px;font-size:.8rem;line-height:1.35');
      const info = taEl('div', '');
      info.append(taEl('strong', 'display:block', p.name + (p.opening_hours === '24/7' ? ' · 24/7' : '')));
      info.append(taEl('span', 'display:block', [[a.street, a.building_number].filter(Boolean).join(' '), [a.post_code, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')));
      if (p.location_description) info.append(taEl('span', 'display:block;color:#4a5a63', p.location_description));
      const b = taEl('button', taBtn, t.choose);
      b.type = 'button';
      b.addEventListener('click', () => taSaveChoice(el, p));
      row.append(info, b);
      res.append(row);
    });
  } catch (e) {
    res.replaceChildren();
    const p = taEl('p', 'margin:6px 0;font-size:.8rem', t.error + ' ');
    const a = taEl('a', 'color:#12305c;font-weight:600;text-decoration:underline', t.map);
    a.href = 'https://inpost.pl/znajdz-paczkomat'; a.target = '_blank'; a.rel = 'noopener';
    p.append(a);
    res.append(p);
  }
}

function taRunQuery(el) {
  const q = (el.querySelector('.ta-pm-q') || {}).value || '';
  const v = q.trim();
  if (!v) return;
  const pc = v.match(/^(\d{2})-?(\d{3})$/);
  if (pc) taSearch(el, { relative_post_code: pc[1] + '-' + pc[2] });
  else taSearch(el, { city: v });
}

function taEnhanceCartNotes() {
  const t = taT();
  document.querySelectorAll('cart-note').forEach((el) => {
    const label = el.querySelector('.cart-note__label');
    if (label && label.textContent.trim() !== t.label) label.textContent = t.label;
    const details = el.querySelector('details');
    if (details && !details.dataset.taOpened) { details.dataset.taOpened = '1'; details.open = true; }
    const ta = el.querySelector('textarea');
    if (ta && ta.placeholder !== t.noteLabel) ta.placeholder = t.noteLabel;
    const inner = el.querySelector('.cart-note__inner');
    if (!inner || inner.querySelector('.ta-pm')) return;
    const box = taEl('div', 'font-size:.8rem;line-height:1.4;margin-bottom:8px;color:#14243a');
    box.className = 'ta-pm';
    box.append(taEl('p', 'margin:0 0 6px', t.info));
    const form = taEl('div', 'display:flex;gap:6px;flex-wrap:wrap;align-items:center');
    const q = taEl('input', 'flex:1 1 140px;min-width:0;border:1px solid #d9e1ee;border-radius:8px;padding:6px 8px;font-size:.85rem');
    q.className = 'ta-pm-q'; q.type = 'text'; q.placeholder = t.ph; q.setAttribute('aria-label', t.ph);
    q.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); taRunQuery(el); } });
    const s = taEl('button', taBtn, t.search); s.type = 'button';
    s.addEventListener('click', () => taRunQuery(el));
    form.append(q, s);
    if (navigator.geolocation) {
      const n = taEl('button', taBtn2, t.near); n.type = 'button';
      n.addEventListener('click', () => navigator.geolocation.getCurrentPosition(
        (pos) => taSearch(el, { relative_point: pos.coords.latitude.toFixed(5) + ',' + pos.coords.longitude.toFixed(5) }),
        () => q.focus()
      ));
      form.append(n);
    }
    const chosen = taEl('div', 'display:none;margin-top:8px;padding:6px 8px;background:#eaf4ea;border-radius:6px');
    chosen.className = 'ta-pm-chosen';
    const res = taEl('div', 'max-height:300px;overflow:auto');
    res.className = 'ta-pm-res';
    const warn = taEl('p', 'margin:8px 0 0;padding:6px 8px;background:#fff6e0;border-radius:6px', '⚠️ ' + t.warn);
    box.append(form, chosen, res, warn);
    inner.prepend(box);
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem('taPaczkomat') || 'null'); } catch (e) {}
    if (!saved && ta) {
      const m = (ta.value || '').match(/^Paczkomat: (\S+)(?: – (.*))?$/m);
      if (m) saved = { name: m[1], addr: m[2] || '' };
    }
    if (saved) taRenderChosen(el, saved);
  });
}
let taTimer = null;
const taSchedule = () => { clearTimeout(taTimer); taTimer = setTimeout(taEnhanceCartNotes, 50); };
taSchedule();
new MutationObserver(taSchedule).observe(document.documentElement, { childList: true, subtree: true });
